import React, { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ChevronRight, Play, Sparkles, Target, Users, CheckCircle, Layers, ListTodo, Brain } from 'lucide-react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Typewriter } from '../animations/text-reveal'

interface HeroSectionProps {
  onChangeBusinessType?: () => void
}

const HeroSection: React.FC<HeroSectionProps> = ({ onChangeBusinessType }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  })

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"])
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  // Floating particles effect
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
  }))

  return (
    <section 
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1d366e 100%)'
      }}
    >
      {/* Animated Background Effects */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500 rounded-full mix-blend-screen filter blur-[100px] animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-indigo-600 rounded-full mix-blend-screen filter blur-[100px] animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-[450px] h-[450px] bg-cyan-500 rounded-full mix-blend-screen filter blur-[100px] animate-blob animation-delay-4000"></div>
        </div>

        {/* Floating Particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)`,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Top gradient fade */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/20 to-transparent"></div>
      </div>

      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 py-6">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/10 backdrop-blur-sm p-1">
              <img src="/logo.svg" alt="VarkWave" className="w-full h-full object-contain" />
            </div>
            <span className="text-2xl font-bold text-white">VarkWave</span>
          </motion.div>

          <div className="flex items-center gap-4">
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden md:flex items-center gap-8"
          >
            <a href="#features" className="text-white/80 hover:text-white transition-colors">Features</a>
            <a href="#customers" className="text-white/80 hover:text-white transition-colors">Customers</a>
            <a href="#integrations" className="text-white/80 hover:text-white transition-colors">Integrations</a>
            <a href="#contact" className="text-white/80 hover:text-white transition-colors">Contact</a>
            {onChangeBusinessType && (
              <button
                type="button"
                onClick={onChangeBusinessType}
                className="text-white/80 hover:text-white transition-colors text-sm"
              >
                View other solutions
              </button>
            )}
            <Button 
              variant="glass" 
              size="sm"
              className="border-white/20 text-white hover:bg-white/20"
              onClick={() => (window.location.href = 'https://campus.varkwave.com')}
            >
              Login
            </Button>
          </motion.div>
          <Button
            variant="glass"
            size="sm"
            className="md:hidden border-white/20 text-white hover:bg-white/20"
            onClick={() => (window.location.href = 'https://campus.varkwave.com')}
          >
            Login
          </Button>
          </div>
        </div>
      </nav>

      <motion.div 
        style={{ y, opacity }}
        className="container mx-auto px-6 relative z-10 pt-20"
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-sm font-medium mb-6"
            >
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span>All-in-One Business Management</span>
            </motion.div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="block"
              >
                Manage it{' '}
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  ALL
                </span>
              </motion.span>
            </h1>
            
            {/* Dynamic Typewriter Subtitle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-xl md:text-2xl text-white/60 mb-6 h-8"
            >
              <Typewriter 
                texts={[
                  "Smart Lead Management",
                  "Custom Stages & Attributes",
                  "Client Relationship Management",
                  "Everything your business needs"
                ]}
                speed={80}
                deleteSpeed={30}
                delayBetween={2500}
              />
            </motion.div>

            {/* Description */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="text-lg md:text-xl text-white/70 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              <span className="font-semibold text-cyan-400">Smart Lead Management</span> with custom stages and attributes, powerful <span className="font-semibold text-blue-400">Client Relationship Management</span> — all in one beautiful platform built for modern businesses.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Button 
                size="xl" 
                className="group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 border-0"
                onClick={() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
                  // Store request type in sessionStorage
                  sessionStorage.setItem('requestType', 'free-trial')
                }}
              >
                Request Free Trial
                <ChevronRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              
              <Button 
                size="xl" 
                variant="ghost"
                className="group text-white border border-white/20 hover:bg-white/10"
                onClick={() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
                  // Store request type in sessionStorage
                  sessionStorage.setItem('requestType', 'demo')
                }}
              >
                <Play className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" />
                Request Demo
              </Button>
            </motion.div>

            {/* Feature Cards - Enhanced Design */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12"
            >
              {[
                { 
                  icon: Brain, 
                  title: 'Smart Lead Management', 
                  desc: 'AI-powered insights',
                  gradient: 'from-cyan-500/20 to-blue-500/20',
                  borderColor: 'border-cyan-500/30',
                  iconColor: 'text-cyan-400',
                  iconBg: 'bg-cyan-500/20'
                },
                { 
                  icon: Layers, 
                  title: 'Custom Stages & Attributes', 
                  desc: 'Tailor-made workflows',
                  gradient: 'from-purple-500/20 to-indigo-500/20',
                  borderColor: 'border-purple-500/30',
                  iconColor: 'text-purple-400',
                  iconBg: 'bg-purple-500/20'
                },
                { 
                  icon: Users, 
                  title: 'Client Relationship Management', 
                  desc: 'Build lasting connections',
                  gradient: 'from-blue-500/20 to-cyan-500/20',
                  borderColor: 'border-blue-500/30',
                  iconColor: 'text-blue-400',
                  iconBg: 'bg-blue-500/20'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: 1.2 + index * 0.15,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ 
                    y: -8, 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                  className={`relative group bg-gradient-to-br ${item.gradient} backdrop-blur-sm rounded-2xl p-5 border ${item.borderColor} hover:border-opacity-60 transition-all duration-300 overflow-hidden`}
                >
                  {/* Animated Background Glow */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl`}></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                    </div>
                    <h3 className="text-base font-bold text-white mb-1 group-hover:text-cyan-100 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-white/60">
                      {item.desc}
                    </p>
                  </div>

                  {/* Shine Effect on Hover */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-1000 ease-in-out transform -skew-x-12"></div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Dashboard Preview */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            {/* Main Dashboard Card */}
            <Card 
              variant="glass" 
              className="relative z-10 bg-white/10 backdrop-blur-xl border-white/20 transform rotate-2 hover:rotate-0 transition-transform duration-500"
              hover3d
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-white/50 ml-auto font-mono">VarkWave Dashboard</span>
                </div>
                
                <div className="space-y-5">
                  {/* Pipeline Stages */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-white">Sales Pipeline</span>
                      <span className="text-xs text-cyan-400">Live</span>
                    </div>
                    <div className="flex gap-2">
                      {['New', 'Qualified', 'Proposal', 'Won'].map((stage, i) => (
                        <div key={stage} className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                          <motion.div 
                            className={`h-full rounded-full ${
                              i === 0 ? 'bg-blue-400' : 
                              i === 1 ? 'bg-yellow-400' : 
                              i === 2 ? 'bg-orange-400' : 'bg-green-400'
                            }`}
                            initial={{ width: "0%" }}
                            animate={{ width: `${[85, 65, 45, 30][i]}%` }}
                            transition={{ duration: 1.5, delay: 1.2 + i * 0.2 }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      {['New', 'Qualified', 'Proposal', 'Won'].map((stage) => (
                        <span key={stage} className="text-xs text-white/40">{stage}</span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-medium text-white/60">Active Leads</span>
                      </div>
                      <div className="text-2xl font-bold text-white">847</div>
                      <div className="text-xs text-green-400 mt-1">+12% this week</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <ListTodo className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs font-medium text-white/60">Tasks Due</span>
                      </div>
                      <div className="text-2xl font-bold text-white">23</div>
                      <div className="text-xs text-yellow-400 mt-1">5 urgent</div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="space-y-2">
                    {[
                      { name: 'New lead assigned', time: '2m ago', color: 'bg-cyan-400' },
                      { name: 'Task completed', time: '5m ago', color: 'bg-green-400' },
                      { name: 'Stage updated', time: '12m ago', color: 'bg-indigo-400' }
                    ].map((activity, i) => (
                      <motion.div 
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.5 + i * 0.1 }}
                        className="flex items-center gap-3 p-2 rounded-lg bg-white/5"
                      >
                        <div className={`w-2 h-2 rounded-full ${activity.color}`}></div>
                        <span className="text-sm text-white/70 flex-1">{activity.name}</span>
                        <span className="text-xs text-white/40">{activity.time}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Floating Cards */}
            <motion.div
              className="absolute -top-6 -right-6 z-20"
              animate={{ 
                y: [0, -8, 0],
                rotate: [0, 3, 0]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-4 shadow-xl shadow-cyan-500/20">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-white" />
                  <div>
                    <div className="text-sm font-semibold text-white">Deal Closed!</div>
                    <div className="text-xs text-white/70">₹2.5L value</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="absolute -bottom-4 -left-6 z-20"
              animate={{ 
                y: [0, 8, 0],
                rotate: [0, -3, 0]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-4 border border-white/20">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">WhatsApp Sent</div>
                    <div className="text-xs text-white/50">via WATI</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <motion.div 
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-white/50 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}

export default HeroSection
